// src/features/settings/components/overenskomster-panel/OverenskomsterGrid.jsx
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Chip, IconButton } from '@mui/material';
import { Delete as DeleteIcon, Edit as EditIcon } from '@mui/icons-material';
import { DataGrid } from '@mui/x-data-grid';

export default function OverenskomsterGrid({ overenskomster, loading, canUpdate, canDelete, onEdit, onDelete }) {
  const { t } = useTranslation(['settings', 'common']);

  const columns = useMemo(() => [
    {
      field: 'code',
      headerName: t('settings:overenskomster.grid.code'),
      width: 90,
    },
    {
      field: 'name',
      headerName: t('settings:overenskomster.grid.name'),
      flex: 1,
      minWidth: 160,
    },
    {
      field: 'valid_from',
      headerName: t('settings:overenskomster.grid.valid_from'),
      width: 110,
    },
    {
      field: 'valid_to',
      headerName: t('settings:overenskomster.grid.valid_to'),
      width: 110,
    },
    {
      field: 'pension_rate_employer',
      headerName: t('settings:overenskomster.grid.pension_rate_employer'),
      width: 140,
      renderCell: (params) => `${params.value} %`,
    },
    {
      field: 'pension_rate_employee',
      headerName: t('settings:overenskomster.grid.pension_rate_employee'),
      width: 140,
      renderCell: (params) => `${params.value} %`,
    },
    {
      field: 'fritvalg_pct',
      headerName: t('settings:overenskomster.grid.fritvalg_pct'),
      width: 100,
      renderCell: (params) => `${params.value} %`,
    },
    {
      field: 'barsel_foedende_weeks',
      headerName: t('settings:overenskomster.grid.barsel_foedende_weeks'),
      width: 120,
    },
    {
      field: 'barsel_ikke_foedende_weeks',
      headerName: t('settings:overenskomster.grid.barsel_ikke_foedende_weeks'),
      width: 120,
    },
    {
      field: 'assumes_scheduled_break',
      headerName: t('settings:overenskomster.grid.assumes_scheduled_break'),
      width: 120,
      sortable: false,
      renderCell: (params) => (
        <Chip
          label={params.value ? t('common:yes') : t('common:no')}
          color={params.value ? 'success' : 'default'}
          size="small"
          variant="outlined"
        />
      ),
    },
    {
      field: 'actions',
      renderHeader: () => (<></>),
      width: 80,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Box>
          {canUpdate && (
            <IconButton
              size="small"
              onClick={() => onEdit(params.row)}
              title={t('settings:overenskomster.buttons.edit')}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          )}
          {canDelete && (
            <IconButton
              size="small"
              onClick={() => onDelete(params.row)}
              title={t('common:buttons.delete')}
              color="error"
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
      ),
    },
  ], [t, canUpdate, canDelete, onEdit, onDelete]);

  return (
    <DataGrid
      rows={overenskomster}
      columns={columns}
      getRowId={(row) => row.overenskomst_id}
      loading={loading}
      pageSizeOptions={[10, 25]}
      initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
      disableColumnFilter
      disableColumnSorting
      disableColumnMenu
      disableColumnResize
      disableRowSelectionOnClick
      sx={{ minHeight: 300 }}
    />
  );
}
